/**
 * State name mapping utility
 * Converts state abbreviations and truncated names to full state names
 */

export const STATE_ABBREVIATIONS = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};

export const STATE_OPTIONS = Object.entries(STATE_ABBREVIATIONS).map(([code, name]) => ({
  code,
  name,
  label: `${code} - ${name}`,
}));

// Map truncated state names (first 3 letters) to full names.
// We derive this from STATE_ABBREVIATIONS so every one of the 50 states
// has a 3-letter key like "Cal" -> "California", "Ari" -> "Arizona", etc.
// If multiple states share the same first 3 letters (e.g. "New ..."),
// the first one in STATE_ABBREVIATIONS wins as the default.
const TRUNCATED_STATE_NAMES = Object.values(STATE_ABBREVIATIONS).reduce((acc, fullName) => {
  const key = fullName.substring(0, 3);
  if (!acc[key]) {
    acc[key] = fullName;
  }
  return acc;
}, {});

/**
 * Convert state abbreviation or truncated name to full state name
 * @param {string} state - State abbreviation (e.g., "CA") or truncated name (e.g., "Cal")
 * @returns {string} Full state name (e.g., "California")
 */
export const getFullStateName = state => {
  if (!state || typeof state !== 'string') {
    return state || '';
  }

  const trimmed = state.trim();

  // Check if it's already a full state name (more than 3 characters and not an abbreviation)
  if (trimmed.length > 3 && !STATE_ABBREVIATIONS[trimmed.toUpperCase()]) {
    // Check if it matches a full state name
    const fullName = Object.values(STATE_ABBREVIATIONS).find(
      name => name.toLowerCase() === trimmed.toLowerCase()
    );
    if (fullName) {
      return fullName;
    }
    // If it's a long string that doesn't match, return as-is (might be a city or full location)
    return trimmed;
  }

  // Check abbreviation first (2 letters, uppercase)
  const upperTrimmed = trimmed.toUpperCase();
  if (STATE_ABBREVIATIONS[upperTrimmed]) {
    return STATE_ABBREVIATIONS[upperTrimmed];
  }

  // Check truncated names (first 3 letters)
  const firstThree = trimmed.substring(0, 3);
  if (TRUNCATED_STATE_NAMES[firstThree]) {
    return TRUNCATED_STATE_NAMES[firstThree];
  }

  // If no match, return original
  return trimmed;
};

export const getStateCode = state => {
  if (!state || typeof state !== 'string') {
    return '';
  }

  const trimmed = state.trim();
  const upperTrimmed = trimmed.toUpperCase();

  if (STATE_ABBREVIATIONS[upperTrimmed]) {
    return upperTrimmed;
  }

  const fullNameMatch = Object.entries(STATE_ABBREVIATIONS).find(
    ([, name]) => name.toLowerCase() === trimmed.toLowerCase()
  );

  return fullNameMatch ? fullNameMatch[0] : '';
};

/**
 * Format location string, expanding state abbreviations/truncations
 * @param {string} location - Location string (e.g., "Los Angeles, Cal" or "Atlanta, GA")
 * @returns {string} Formatted location with full state name (e.g., "Los Angeles, California")
 */
export const formatLocation = location => {
  if (!location || typeof location !== 'string') {
    return location || '';
  }

  // Split by comma to separate city and state
  const parts = location.split(',').map(part => part.trim());

  if (parts.length >= 2) {
    const city = parts[0];
    const state = parts.slice(1).join(', '); // Handle cases with multiple commas
    const fullStateName = getFullStateName(state);
    return `${city}, ${fullStateName}`;
  }

  // If no comma, check if the whole string is a state
  const fullStateName = getFullStateName(location);
  return fullStateName !== location ? fullStateName : location;
};

/**
 * Format location string with a 2-letter state code (e.g., "Los Angeles, CA").
 * @param {string} location - Location string (e.g., "Los Angeles, California" or "Atlanta, GA")
 * @returns {string} Formatted location with 2-letter state code
 */
export const formatLocationShort = location => {
  if (!location || typeof location !== 'string') {
    return location || '';
  }

  const text = location
    .split('|')
    .map(part => part.trim())
    .filter(Boolean)
    .pop();

  if (!text) {
    return '';
  }

  const countryTokens = /^(usa|us|u\.s\.a\.|u\.s\.|united states|united states of america)$/i;
  const parts = text
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  if (parts.length > 2 && countryTokens.test(parts[parts.length - 1])) {
    parts.pop();
  }

  if (parts.length >= 2) {
    const city = parts[parts.length - 2];
    const stateRaw = parts[parts.length - 1].replace(/\b\d{5}(?:-\d{4})?\b/g, '').trim();
    const stateCode = getStateCode(stateRaw);
    return stateCode ? `${city}, ${stateCode}` : `${city}, ${stateRaw}`;
  }

  // Single token: try to interpret as a state, else return as-is
  const stateCode = getStateCode(text);
  return stateCode || text;
};
